import { useEffect, useRef } from 'react';
import { useLazyGetBookingDetailsQuery } from '../../../../../api/front-office/reports.api.rtk';
import {
  dateFormat,
  formatNumber,
  pdfValuesToString,
  toReportInputs,
} from '../../../../../utils/format';
import useToast from '../../../../../hooks/useToast';
import FormPDF, { IFormInput, IFormPDFRef } from '@components/pdf/form-pdf';
import useAppContext from '../../../../../hooks/useAppContext';
import moment from 'moment';

export const PDFReservationForm = ({
  handleModalClose,
  bookingReferenceId,
  isOpen,
  hideRoomRate,
  isBlank,
}: {
  handleModalClose?: () => void;
  bookingReferenceId?: string;
  hideRoomRate?: boolean;
  isOpen: boolean;
  isBlank?: boolean;
}) => {
  const { startLoading, stopLoading } = useAppContext();
  const { toast } = useToast();
  const [getBookingDetails] = useLazyGetBookingDetailsQuery();
  const formRef = useRef<IFormPDFRef>(null);

  const getBooking = async (bookingReferenceId: string, isBlank) => {
    try {
      if (isBlank) return { booking: null, terms: null };

      const result = await getBookingDetails(
        `?bookingReferenceId=${bookingReferenceId}`,
      ).unwrap();

      if (!result) return { booking: null, terms: null };

      return {
        booking: pdfValuesToString(result.data.folio?.[0]),
        terms: result.data.folio?.[1],
      };
    } catch (error) {
      toast({ message: error, severity: 'warning' });
      return { booking: null, terms: null };
    }
  };

  const handlePrint = async () => {
    try {
      startLoading();

      if (!formRef.current) {
        toast({ severity: 'warning', message: 'No PDF Component Found' });
        return;
      }

      const { getHeaderDetails, getPropertyLogo, handleShowPDF, getTemplate } =
        formRef.current;

      const template = await getTemplate({
        department: 'FO',
        category: 'Reservations',
        templateName: 'Registration',
      });

      if (!template) return;

      const { booking, terms } = await getBooking(bookingReferenceId!, isBlank);

      const { propertyLogo } = await getPropertyLogo();
      const headerDetails = getHeaderDetails({ moduleName: 'Xenia FOS' });
      console.log({ booking });
      const inputs: IFormInput = {
        ...(propertyLogo && { propertyLogo }),
        ...headerDetails,
        ...toReportInputs(booking ?? {}, isBlank),
        guestName: booking?.guestName ?? ' ',
        contact: booking?.contact ?? ' ',
        email: booking?.email ?? ' ',
        company: booking?.company ?? ' ',
        pax: booking?.pax ?? ' ',
        marketSegment: booking?.marketSegment ?? ' ',
        sharer: booking?.sharer ?? ' ',
        room: booking?.room ?? ' ',
        rate: booking?.projectedRates ?? ' ',
        bookingID: booking?.bookingID ?? ' ',
        businessSource: booking?.businessSource ?? ' ',
        arrival: booking?.arrival
          ? moment(booking.arrival).format(dateFormat)
          : ' ',
        departure: booking?.departure
          ? moment(booking.departure).format(dateFormat)
          : ' ',
        ...(terms && {
          renderList: JSON.stringify(
            terms.propertyTermsAndConditions?.[0]?.tnc_body?.body?.content?.[0]
              ?.bodyContent,
          ),
        }),
      };

      if (isBlank) {
        handleShowPDF({
          template,
          inputs,
        });

        return;
      }

      const { rate, ...etc } = inputs;

      handleShowPDF({
        template,
        inputs: hideRoomRate
          ? { ...etc }
          : { ...etc, rate: formatNumber(rate.toString(), 2, 2) },
      });
    } catch (error) {
      handleModalClose?.();
      toast({ message: error, severity: 'warning' });
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (!isOpen || (!bookingReferenceId && !isBlank)) return;

    (async () => await handlePrint())();
  }, [isOpen, bookingReferenceId, isBlank]);

  return (
    <FormPDF
      {...{
        ref: formRef,
        title: 'Registration Form',
        modalOnClose: handleModalClose,
      }}
    />
  );
};
